import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Send, Sparkles, CheckCircle, Target, MapPin, DollarSign, TrendingUp } from 'lucide-react'
import { candidateDeepDive } from '@/features/candidates/data/mockData'

const PROGRESS_STEPS = [
  { label: 'Role Goals',    done: true },
  { label: 'Location',      done: true },
  { label: 'Progression',   done: true },
  { label: 'Culture Fit',   done: true },
  { label: 'Compensation',  done: false },
  { label: 'Constraints',   done: false },
  { label: 'Motivations',   done: false },
]

const COMPLETION = Math.round(
  (PROGRESS_STEPS.filter((s) => s.done).length / PROGRESS_STEPS.length) * 100
)

const SUMMARY_ITEMS = [
  { icon: Target,     label: 'Target Roles',    value: 'GM Expansion, VP Sales' },
  { icon: MapPin,     label: 'Location',         value: 'Riyadh or Dubai' },
  { icon: DollarSign, label: 'Compensation',     value: 'Base $250k + upside' },
  { icon: TrendingUp, label: 'Progression',      value: 'Build teams + open markets' },
]

export const CandidateDeepDiveConversation = () => {
  const navigate = useNavigate()
  const [messages, setMessages] = useState(candidateDeepDive.conversation)
  const [input, setInput] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)

  const handleSend = () => {
    const trimmed = input.trim()
    if (!trimmed) return
    setMessages((prev) => [
      ...prev,
      { id: `user-${Date.now()}`, speaker: 'candidate' as const, text: trimmed, createdAt: new Date().toISOString() },
    ])
    setInput('')
    setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth' }), 50)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  return (
    <div className="bg-[#02182B] min-h-screen flex flex-col">
      {/* Header */}
      <div className="border-b border-[#233D4C] sticky top-0 z-30 bg-[#02182B]">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/candidate/deep-dive/start')} className="text-gray-400 hover:text-white text-sm transition-colors">
            ← Back
          </button>
          <div className="flex items-center gap-2">
            <Sparkles size={15} className="text-[#FD802E]" />
            <span className="text-white font-semibold text-sm">AI Career Deep-Dive</span>
          </div>
          <button
            onClick={() => navigate('/candidate/deep-dive/confirmation')}
            className="bg-[#FD802E] text-[#233D4C] font-semibold text-sm px-4 py-1.5 rounded-lg hover:bg-[#ff8f45] transition-colors"
          >
            Finish & Review
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 gap-6">

        {/* Left — Progress */}
        <div className="hidden lg:block w-52 flex-shrink-0">
          <div className="mb-4">
            <div className="flex justify-between text-xs text-gray-400 mb-1.5">
              <span>Completion</span>
              <span className="text-[#FD802E] font-medium">{COMPLETION}%</span>
            </div>
            <div className="w-full h-1.5 bg-[#2a4a5c] rounded-full overflow-hidden">
              <div className="h-full bg-[#FD802E] rounded-full" style={{ width: `${COMPLETION}%` }} />
            </div>
          </div>
          <nav className="space-y-1">
            {PROGRESS_STEPS.map((step) => (
              <div key={step.label} className="flex items-center gap-2.5 px-2 py-1.5">
                {step.done ? (
                  <CheckCircle size={14} className="text-green-400 shrink-0" />
                ) : (
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-[#2a4a5c] shrink-0" />
                )}
                <span className={`text-sm ${step.done ? 'text-green-400' : 'text-gray-500'}`}>
                  {step.label}
                </span>
              </div>
            ))}
          </nav>
        </div>

        {/* Centre — Chat */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex-1 overflow-y-auto space-y-3 mb-4 max-h-[calc(100vh-320px)] min-h-[280px]">
            {messages.map((msg, i) => (
              <div key={`${msg.speaker}-${i}`} className={`flex ${msg.speaker === 'erica' ? 'justify-start' : 'justify-end'}`}>
                {msg.speaker === 'erica' && (
                  <div className="w-7 h-7 bg-[#FD802E]/10 rounded-full flex items-center justify-center mr-2 mt-1 shrink-0">
                    <Sparkles size={13} className="text-[#FD802E]" />
                  </div>
                )}
                <div className={`max-w-[75%] rounded-xl px-4 py-3 text-sm ${
                  msg.speaker === 'erica'
                    ? 'bg-[#233D4C] text-white'
                    : 'bg-[#FD802E] text-[#233D4C] font-medium'
                }`}>
                  <p className={`text-xs mb-1 ${msg.speaker === 'erica' ? 'text-gray-400' : 'text-[#233D4C]/60'}`}>
                    {msg.speaker === 'erica' ? 'Erica' : 'You'}
                  </p>
                  <p className="leading-relaxed">{msg.text}</p>
                </div>
              </div>
            ))}
            <div ref={scrollRef} />
          </div>

          {/* Input */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-xl p-3 flex gap-3 items-end">
            <textarea
              rows={3}
              placeholder="Type your response… (Enter to send)"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 resize-none bg-transparent text-white text-sm placeholder:text-gray-500 focus:outline-none leading-relaxed"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim()}
              className="bg-[#FD802E] text-[#233D4C] p-2 rounded-lg hover:bg-[#ff8f45] transition-colors disabled:opacity-50 shrink-0"
            >
              <Send size={16} />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1.5 text-center">Press Enter to send · Shift+Enter for new line</p>
        </div>

        {/* Right — Live summary */}
        <div className="hidden lg:block w-64 flex-shrink-0 space-y-3">
          <p className="text-xs uppercase text-gray-400 font-medium">Live Summary</p>
          {SUMMARY_ITEMS.map((item) => (
            <div key={item.label} className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <item.icon size={12} className="text-[#FD802E]" />
                <p className="text-xs uppercase text-gray-400 font-medium">{item.label}</p>
              </div>
              <p className="text-white text-sm">{item.value}</p>
            </div>
          ))}
        </div>

      </div>
    </div>
  )
}
